/* eslint-disable no-unused-vars */
import jwtDecode from 'jwt-decode'
import { createContext, useCallback, useEffect, useReducer } from 'react'
import SplashScreen from 'src/components/SplashScreen'
import { backend } from 'src/config'
import axios from 'src/utils/axios'
import { AUTH_STATUS } from 'src/constants'

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
}

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false
  }

  const decoded = jwtDecode(accessToken)
  const currentTime = Date.now() / 1000

  return decoded.exp > currentTime
}

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken)
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
  } else {
    localStorage.removeItem('accessToken')
    delete axios.defaults.headers.common.Authorization
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      }
    }
    case 'LOGIN': {
      const { user } = action.payload

      return {
        ...state,
        isAuthenticated: true,
        user,
      }
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      }
    }
    case 'REGISTER': {
      const { user } = action.payload

      return {
        ...state,
        isAuthenticated: true,
        user,
      }
    }
    default: {
      return { ...state }
    }
  }
}

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  verifyOtp: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState)

  const login = async (username, password) => {
    const response = await axios.post(`${backend}/api/auth/sign-in`, {
      login: username,
      password,
    })

    const { data } = response

    if (data.message2FA) {
      return { status: AUTH_STATUS.TWO_FA_REQUIRED }
    }

    if (data.token) {
      const { token, ...user } = data
      setSession(token)
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      })

      initialize()
      return { status: AUTH_STATUS.SUCCESS }
    }
  }

  const verifyOtp = async (otp) => {
    const response = await axios.post(`${backend}/api/auth/verify-otp`, {
      otp,
    })
    const { token, ...user } = response.data
    setSession(token)
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    })

    initialize()
  }

  const logout = () => {
    setSession(null)
    dispatch({ type: 'LOGOUT' })
  }

  const initialize = useCallback(async () => {
    try {
      const accessToken = window.localStorage.getItem('accessToken')

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)

        const response = await axios.get(`${backend}/api/auth/current-user`)
        const { password, ...user } = response.data

        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: true,
            user,
          },
        })
      } else {
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    } catch (err) {
      console.error(err)
      dispatch({
        type: 'INITIALISE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      })
    }
  }, [])

  useEffect(() => {
    initialize()
  }, [initialize])

  if (!state.isInitialised) {
    return <SplashScreen />
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        verifyOtp,
        // register
      }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
